import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useGetProgressChronumQuery } from '@shared/api';
import { fmtDateTime, type AttendanceSite, type AttendanceView } from '@entities/progress';
import { blockDrop, riseIn } from '@shared/theme/animations';
import { useCountUp } from '@shared/lib/useCountUp';
import { IconChevronDown, IconUsers } from '@shared/ui/icons';

/* Баннер «Люди на объекте» — вторая идентичность экрана Руководителя рядом с
   его красным hero: то же жидкое стекло, но фиолетовое, чтобы сразу читалось
   «это из Хронума, а не из агентов ОСНОВЫ».

   Площадки в списке — ВСЕ, какие есть у холдинга в Хронуме, а не только те,
   под которые заведён объект ОСНОВЫ: на ВПУ и Базе люди работают, объектов у
   них нет. Открывается баннер на площадке текущего объекта, дальше выбор за
   руководителем. Арифметики здесь нет: выбирается одна площадка или холдинг
   целиком, каждое число Хронум посчитал сам. */

/** Пункт списка: площадка Хронума или холдинг целиком (`id === null`). */
interface PickerItem {
  id: string | null;
  title: string;
  hint: string;
}

/* Фиолетовый — цвет Хронума на этом экране: красный занят идентичностью
   самого агента, поэтому второй источник отличается тоном, а не формой. */
const violet = (alpha: number) => `rgba(139, 92, 246, ${alpha})`;
const violetSoft = (alpha: number) => `rgba(167, 139, 250, ${alpha})`;

const Banner = styled.section`
  position: relative;
  overflow: visible; /* список площадок — в портале поверх страницы, см. PickerPanel */
  border-radius: ${({ theme }) => theme.radius.xl};
  background: linear-gradient(
    135deg,
    ${violet(0.17)} 0%,
    ${violet(0.09)} 52%,
    ${violetSoft(0.12)} 100%
  );
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  border: 1px solid ${violetSoft(0.3)};
  box-shadow:
    0 16px 40px -18px rgba(0, 0, 0, 0.45),
    0 0 34px -22px ${violetSoft(0.3)},
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  padding: ${({ theme }) => `${theme.spacing(6)} ${theme.spacing(7)}`};
  animation: ${riseIn} 460ms ${({ theme }) => theme.motion.easeOut} both;

  /* Фиолетовый «свет из-за стекла» — тот же приём, что у красного hero. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background:
      radial-gradient(80% 130% at 85% -20%, ${violetSoft(0.22)} 0%, ${violetSoft(0)} 60%),
      radial-gradient(70% 130% at 8% 120%, ${violet(0.16)} 0%, ${violet(0)} 55%);
    pointer-events: none;
  }

  @media (max-width: 760px) {
    padding: ${({ theme }) => `${theme.spacing(5)} ${theme.spacing(5)}`};
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(6)};
  flex-wrap: wrap;
`;

const Identity = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(4)};
  min-width: 0;
`;

const Emblem = styled.div`
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: ${({ theme }) => theme.radius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${violetSoft(1)};
  background: ${violet(0.12)};
  border: 1px solid ${violetSoft(0.32)};
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.18),
    0 0 14px -8px ${violetSoft(0.4)};
  transform-origin: bottom center;
  animation: ${blockDrop} 540ms ${({ theme }) => theme.motion.spring} both;

  @media (max-width: 760px) {
    width: 46px;
    height: 46px;
  }
`;

const Titles = styled.div`
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 23px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: 760px) {
    font-size: 19px;
  }
`;

const Note = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 520px;
`;

/* ── Выпадающий список площадок ─────────────────────────────────────────── */

const PickerWrap = styled.div`
  position: relative;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const PickerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  max-width: 100%;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${violet(0.12)};
  border: 1px solid ${violetSoft(0.34)};
  border-radius: ${({ theme }) => theme.radius.pill};
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${violet(0.2)};
    border-color: ${violetSoft(0.5)};
  }

  &:focus-visible {
    outline: 2px solid transparent;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  svg {
    flex-shrink: 0;
    color: ${violetSoft(1)};
  }
`;

const PickerValue = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** Где рисовать список: под кнопкой (`top`) или над ней (`bottom`), px окна. */
interface Placement {
  top: number | null;
  bottom: number | null;
  left: number;
  width: number;
  maxHeight: number;
}

const PANEL_WIDTH = 290;
const PANEL_MAX_HEIGHT = 340;
/** Меньше места снизу — открываем вверх, если сверху его больше. */
const PANEL_MIN_ROOM = 220;
const VIEWPORT_GAP = 8;

/** Расположение списка у кнопки: вниз или вверх, не за краем окна. */
function placementFor(button: DOMRect, vw: number, vh: number): Placement {
  const below = vh - button.bottom - VIEWPORT_GAP;
  const above = button.top - VIEWPORT_GAP;
  const up = below < PANEL_MIN_ROOM && above > below;
  const room = Math.max(140, Math.min(PANEL_MAX_HEIGHT, (up ? above : below) - 6));
  const width = Math.min(Math.max(PANEL_WIDTH, button.width), vw - 2 * VIEWPORT_GAP);
  const left = Math.max(VIEWPORT_GAP, Math.min(button.left, vw - width - VIEWPORT_GAP));
  return {
    top: up ? null : button.bottom + 6,
    bottom: up ? vh - button.top + 6 : null,
    left,
    width,
    maxHeight: room,
  };
}

function samePlacement(a: Placement | null, b: Placement): boolean {
  return (
    !!a &&
    a.top === b.top &&
    a.bottom === b.bottom &&
    a.left === b.left &&
    a.width === b.width &&
    a.maxHeight === b.maxHeight
  );
}

/* Список — в портале (`document.body`) с `position: fixed` по кнопке, как у
   `Combobox`. Баннер держит свой контекст наложения (`backdrop-filter`,
   анимация с `transform`), и абсолютный список с любым z-index уходил под
   подпись баннера и карточки объектов ниже. Координаты — через `style`
   (attrs), без генерации классов на каждую прокрутку. */
const PickerPanel = styled.ul.attrs<{ $place: Placement }>(({ $place }) => ({
  style: {
    top: $place.top ?? undefined,
    bottom: $place.bottom ?? undefined,
    left: $place.left,
    width: $place.width,
    maxHeight: $place.maxHeight,
  },
}))<{ $place: Placement }>`
  position: fixed;
  z-index: ${({ theme }) => theme.z.modal + 10};
  overflow-y: auto;
  margin: 0;
  padding: ${({ theme }) => theme.spacing(1)};
  list-style: none;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${violetSoft(0.3)};
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  animation: ${riseIn} 160ms ${({ theme }) => theme.motion.easeOut} both;
`;

const PickerItemButton = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
  text-align: left;
  font: inherit;
  cursor: pointer;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ $active }) => ($active ? violet(0.16) : 'transparent')};
  color: ${({ theme }) => theme.colors.text.primary};

  &:hover {
    background: ${violet(0.12)};
  }

  &:focus-visible {
    outline: 2px solid transparent;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  small {
    font-size: 11.5px;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

/* ── Числа ──────────────────────────────────────────────────────────────── */

const StatStrip = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2.5)};
  flex-wrap: wrap;
`;

const Chip = styled.div<{ $delay: number; $warn?: boolean }>`
  min-width: 104px;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ $warn, theme }) => ($warn ? `${theme.colors.status.warning}22` : violet(0.1))};
  border: 1px solid
    ${({ $warn, theme }) => ($warn ? `${theme.colors.status.warning}55` : violetSoft(0.24))};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px) saturate(150%);
  -webkit-backdrop-filter: blur(8px) saturate(150%);
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut} both;
  animation-delay: ${({ $delay }) => `${$delay}ms`};
`;

const ChipValue = styled.div`
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
`;

const ChipLabel = styled.div`
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Foot = styled.div`
  position: relative;
  z-index: 1;
  margin-top: ${({ theme }) => theme.spacing(4)};
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Число чипа с анимацией count-up — как в hero агента. */
function ChipNumber({ value }: { value: number }) {
  const n = useCountUp(value);
  return <>{Math.round(n).toLocaleString('ru-RU')}</>;
}

/** «12:18» из `2026-10-19T12:18:00+05:00` — без разбора даты и зоны. */
function localClock(localTime: string | null): string | null {
  if (!localTime || localTime.length < 16) return null;
  return localTime.slice(11, 16);
}

/** Пояс в подписи: «Asia/Irkutsk» → «Irkutsk». */
function tzCity(timezone: string | null): string | null {
  if (!timezone) return null;
  const tail = timezone.split('/').pop();
  return tail ? tail.replace(/_/g, ' ') : null;
}

function holdingItem(view: AttendanceView): PickerItem {
  const h = view.holding;
  return {
    id: null,
    title: 'Весь холдинг',
    hint: h
      ? `на смене ${h.on_shift_now} из ${h.workers} работающих · ${h.sites} площадок`
      : 'итоги по всем компаниям',
  };
}

function siteItem(site: AttendanceSite): PickerItem {
  return {
    id: site.id,
    title: site.name,
    hint: `на смене ${site.on_shift_now} из ${site.workers_attached} закреплённых`,
  };
}

interface Props {
  /** Объект ОСНОВЫ, с площадок которого открыть баннер (может быть не выбран). */
  projectId?: string;
}

export function ChronumBanner({ projectId }: Props) {
  const { data, isFetching } = useGetProgressChronumQuery(projectId);
  const [open, setOpen] = useState(false);
  /** `undefined` — выбора ещё не было, берём площадку объекта. */
  const [choice, setChoice] = useState<string | null | undefined>(undefined);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLUListElement>(null);
  const [place, setPlace] = useState<Placement | null>(null);
  const listId = useId();

  // Сменили объект — баннер снова открывается на его площадке.
  useEffect(() => {
    setChoice(undefined);
    setOpen(false);
  }, [projectId]);

  // Список в портале следует за кнопкой: прокрутка любого контейнера и ресайз окна.
  const reposition = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    const next = placementFor(el.getBoundingClientRect(), window.innerWidth, window.innerHeight);
    setPlace((prev) => (samePlacement(prev, next) ? prev : next));
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  // Клик вне кнопки и списка и Esc закрывают его (список — в портале, вне wrapRef).
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      if (!wrapRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const items: PickerItem[] = useMemo(() => {
    if (!data) return [];
    return [holdingItem(data), ...data.sites.map(siteItem)];
  }, [data]);

  if (!data) return null;

  // Площадка объекта, если руководитель не выбрал другую.
  const defaultSiteId = data.object_site_ids[0] ?? null;
  const siteId = choice === undefined ? defaultSiteId : choice;
  const site = siteId ? (data.sites.find((s) => s.id === siteId) ?? null) : null;
  const holding = data.holding;
  const selected = items.find((i) => i.id === (site ? site.id : null)) ?? items[0];

  const numbers = site
    ? {
        onShift: site.on_shift_now,
        attended: site.attended_today,
        pool: site.workers_attached,
        poolLabel: 'Закреплено за площадкой',
        stale: site.open_shifts_stale,
      }
    : holding && {
        onShift: holding.on_shift_now,
        attended: holding.attended_today,
        pool: holding.workers,
        poolLabel: 'Работают в холдинге',
        stale: holding.open_shifts_stale,
      };

  const clock = localClock(site?.local_time ?? null);
  const city = tzCity(site?.timezone ?? null);

  return (
    <Banner aria-label="Люди на объекте по данным Хронума">
      <Inner>
        <Identity>
          <Emblem aria-hidden>
            <IconUsers size={24} />
          </Emblem>
          <Titles>
            <Title>Люди на объекте</Title>
            {data.available && items.length > 0 && (
              <PickerWrap ref={wrapRef}>
                <PickerButton
                  ref={buttonRef}
                  type="button"
                  onClick={() => setOpen((v) => !v)}
                  aria-haspopup="listbox"
                  aria-expanded={open}
                  aria-controls={open ? listId : undefined}
                  title="Выбрать площадку Хронума"
                >
                  <PickerValue>{selected?.title ?? 'Весь холдинг'}</PickerValue>
                  <IconChevronDown size={14} />
                </PickerButton>
                {open &&
                  place &&
                  createPortal(
                    <PickerPanel
                      ref={panelRef}
                      id={listId}
                      role="listbox"
                      aria-label="Площадки Хронума"
                      $place={place}
                    >
                      {items.map((item) => (
                        <li key={item.id ?? 'holding'} role="none">
                          <PickerItemButton
                            type="button"
                            role="option"
                            aria-selected={item.id === (site ? site.id : null)}
                            $active={item.id === (site ? site.id : null)}
                            onClick={() => {
                              setChoice(item.id);
                              setOpen(false);
                            }}
                          >
                            <span>{item.title}</span>
                            <small>{item.hint}</small>
                          </PickerItemButton>
                        </li>
                      ))}
                    </PickerPanel>,
                    document.body,
                  )}
              </PickerWrap>
            )}
            <Note>{data.note}</Note>
          </Titles>
        </Identity>

        {numbers && (
          <StatStrip aria-label="Люди на смене, отметившиеся и забытые уходы">
            <Chip $delay={120}>
              <ChipValue>
                <ChipNumber value={numbers.onShift} />
              </ChipValue>
              <ChipLabel>На смене сейчас</ChipLabel>
            </Chip>
            <Chip $delay={200}>
              <ChipValue>
                <ChipNumber value={numbers.attended} />
              </ChipValue>
              <ChipLabel>Отметились сегодня</ChipLabel>
            </Chip>
            <Chip $delay={280}>
              <ChipValue>
                <ChipNumber value={numbers.pool} />
              </ChipValue>
              <ChipLabel>{numbers.poolLabel}</ChipLabel>
            </Chip>
            <Chip $delay={360} $warn={numbers.stale > 0}>
              <ChipValue>
                <ChipNumber value={numbers.stale} />
              </ChipValue>
              <ChipLabel>Забыли отметить уход</ChipLabel>
            </Chip>
          </StatStrip>
        )}
      </Inner>

      <Foot>
        {clock && (
          <span>
            местное время площадки {clock}
            {city ? ` (${city})` : ''} — по нему считаются её сутки
          </span>
        )}
        {data.updated_at && <span>Хронум посчитал {fmtDateTime(data.updated_at)}</span>}
        {isFetching && <span>обновляю…</span>}
        <span>источник: {data.source_ref}</span>
      </Foot>
    </Banner>
  );
}
